// Pure helpers for the publish/edit listing date+time picker.
//
// The window is modelled as a START plus a LENGTH, never as two absolute times:
//
//   { date, startTime, durationMin }
//
// The end is always derived (getEndTime / getEndDate). That is what makes a
// window running past midnight ordinary rather than a special case: a duration
// is unsigned, so "23:45 for 2 hours" needs no rule to tell it apart from a
// 22-hour window running backwards. Two-absolute-times needed exactly such a
// rule, and it produced both a picker that went dead near midnight and a start
// wheel that silently stretched the window toward 24h.
//
// It also means moving the start can never resize the window, and there is no
// end-time constraint to compute at all: every duration is valid at every
// start.
//
// All times are LOCAL (matching what the user sees in the picker). The page
// component serializes to the backend's `time_window` shape, which *does* use
// two absolute datetimes, by stamping getEndDate/getEndTime onto the end bound.
//
// String formats:
//   DateStr  → "YYYY-MM-DD"
//   TimeStr  → "HH:mm:ss"

const pad = (n: number) => String(n).padStart(2, '0');
const DAY_MIN = 24 * 60;

export type DateStr = string;
export type TimeStr = string;

export interface TimeWindowState {
  date: DateStr;
  startTime: TimeStr;
  /** Window length in minutes. The end bound is derived from this, not stored. */
  durationMin: number;
}

/** The persisted window an edit is measured against. Same shape as the form's. */
export type OriginalTimeWindow = TimeWindowState;

/** The duration wheel's grid: whole hours against quarter-hours. */
export const DURATION_STEP_MIN = 15;
export const DURATION_HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0..12
export const DURATION_MINUTE_OPTIONS = [0, 15, 30, 45];
export const MIN_DURATION_MIN = DURATION_STEP_MIN;
export const MAX_DURATION_MIN =
  DURATION_HOUR_OPTIONS[DURATION_HOUR_OPTIONS.length - 1] * 60;
export const DEFAULT_DURATION_MIN = 120;
const DEFAULT_START_TIME: TimeStr = '18:30:00';

/**
 * How long a window of a given kind is allowed to run, and what the wheel
 * offers for it. The two hand-off methods want different answers: being home
 * for pickup is an evening-length commitment, while a drop-off is one driving
 * run and stops being a "run" somewhere north of an hour.
 *
 * `hourOptions` / `minuteOptions` are what the wheel RENDERS, not what it
 * accepts. Per the clampWindow note below, no option is ever disabled — the
 * wheel runs free and an out-of-range combination snaps back to `maxMin`. So
 * the drop-off wheel showing a 1-hour column alongside 45 minutes is expected;
 * 1:45 simply settles at 1:00.
 */
export interface DurationBounds {
  minMin: number;
  maxMin: number;
  defaultMin: number;
  hourOptions: number[];
  minuteOptions: number[];
}

/** Pickup: the original wheel range, unchanged. */
export const PICKUP_DURATION_BOUNDS: DurationBounds = {
  minMin: MIN_DURATION_MIN,
  maxMin: MAX_DURATION_MIN,
  defaultMin: DEFAULT_DURATION_MIN,
  hourOptions: DURATION_HOUR_OPTIONS,
  minuteOptions: DURATION_MINUTE_OPTIONS,
};

/**
 * Drop-off: one run, 15 minutes to an hour, defaulting to the recommended 30.
 * The hour column stops at 1 so the wheel itself signals the cap rather than
 * letting the chef scroll to 6 hours and be silently snapped back.
 */
export const DROPOFF_DURATION_BOUNDS: DurationBounds = {
  minMin: DURATION_STEP_MIN,
  maxMin: 60,
  defaultMin: 30,
  hourOptions: [0, 1],
  minuteOptions: DURATION_MINUTE_OPTIONS,
};

// --- Local date/time primitives ---

export function todayLocalDate(): DateStr {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Round minutes DOWN to the nearest 15-minute mark (0, 15, 30, 45). */
function floorTo15(minutes: number): number {
  return Math.floor(minutes / 15) * 15;
}

export function nowLocalTime(): TimeStr {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(floorTo15(d.getMinutes()))}:00`;
}

/** "HH:mm:ss" to minutes-from-midnight. */
function toMinutes(time: TimeStr): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Minutes-from-midnight back to "HH:mm:ss", wrapped into [00:00, 24:00). */
function fromMinutes(total: number): TimeStr {
  const wrapped = ((total % DAY_MIN) + DAY_MIN) % DAY_MIN;
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}:00`;
}

/** Shift "YYYY-MM-DD" by whole days. UTC math so DST can't move the date. */
export function addDays(date: DateStr, days: number): DateStr {
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
}

/**
 * Minutes-from-epoch for a naive "YYYY-MM-DDTHH:mm(:ss)" key. Built on Date.UTC
 * so a DST transition between two keys never skews the difference.
 */
function keyToMinutes(key: string): number {
  const [date, time] = key.split('T');
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  return Date.UTC(y, mo - 1, d) / 60000 + h * 60 + mi;
}

/** A naive local Date for Intl formatting. Display only. */
function toLocalDate(date: DateStr, time: TimeStr): Date {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  return new Date(y, mo - 1, d, h, mi, 0, 0);
}

// --- Derived end bound ---

/** Minutes past midnight OF `date` at which the window ends. May exceed 24h. */
function endOffsetMin(tw: TimeWindowState): number {
  return toMinutes(tw.startTime) + tw.durationMin;
}

/** The window's end wall-clock time. */
export function getEndTime(tw: TimeWindowState): TimeStr {
  return fromMinutes(endOffsetMin(tw));
}

/** The date the window's end falls on: `date`, or the day after. */
export function getEndDate(tw: TimeWindowState): DateStr {
  return addDays(tw.date, Math.floor(endOffsetMin(tw) / DAY_MIN));
}

/** True when the window runs into the following day. */
export function endsNextDay(tw: TimeWindowState): boolean {
  return endOffsetMin(tw) >= DAY_MIN;
}

// --- Duration wheel columns ---

/** Split a duration into the two wheel columns. */
export function splitDuration(durationMin: number): { hours: number; minutes: number } {
  return { hours: Math.floor(durationMin / 60), minutes: durationMin % 60 };
}

/** Join the two wheel columns back into a duration. */
export function joinDuration(hours: number, minutes: number): number {
  return hours * 60 + minutes;
}

/**
 * Round a duration onto the wheel's 15-minute grid, never below one step.
 *
 * Every window the old two-wheel form could produce was already a multiple of
 * 15 (both wheels used `minuteValues="0,15,30,45"`), with one exception: the
 * end used to clamp to "23:59:00" near midnight, which could leave a stored
 * length like 14 minutes. Snapping on the way in keeps the wheel and the
 * readout from ever disagreeing.
 */
function snapDuration(durationMin: number): number {
  const snapped = Math.round(durationMin / DURATION_STEP_MIN) * DURATION_STEP_MIN;
  return Math.max(snapped, DURATION_STEP_MIN);
}

// --- IonDatetime adapter ---
// IonDatetime presentation="time" parses a full ISO string and uses the date
// portion for internal min/max validation. We stamp every time value with the
// same reference date so its validator only compares hours/minutes.
const TIME_REF_DATE = '2020-01-01';

export function withRefDate(time: TimeStr | null | undefined): string | undefined {
  return time ? `${TIME_REF_DATE}T${time}` : undefined;
}

/** Pull "HH:mm:ss" out of whatever IonDatetime hands back. */
export function extractTime(value: string | string[] | null | undefined): TimeStr | null {
  if (typeof value !== 'string') return null;
  return value.includes('T') ? value.split('T')[1] : value;
}

/** Pull "YYYY-MM-DD" out of whatever IonDatetime hands back. */
export function extractDate(value: string | string[] | null | undefined): DateStr | null {
  if (typeof value !== 'string') return null;
  return value.split('T')[0];
}

// --- Server shape ---

/**
 * Convert the backend's `time_window` shape ({ start, end } as naive
 * "YYYY-MM-DDTHH:mm:ss" strings) into the picker's TimeWindowState, measuring
 * the duration between the two absolute bounds. Returns null if either bound is
 * missing/malformed or the window is empty.
 *
 * This is the single source of truth for "what the server says the window is" —
 * both the edit-form seed and the post-save reconciliation go through it so the
 * form can never silently drift from the persisted value. The length is snapped
 * onto the wheel's 15-minute grid (see snapDuration) so the controls and the
 * readout always agree.
 */
export function fromTimeWindow(
  tw: { start?: string; end?: string } | null | undefined,
): TimeWindowState | null {
  if (!tw?.start || !tw?.end) return null;
  const [date, startTime] = tw.start.split('T');
  if (!date || !startTime) return null;
  const durationMin = keyToMinutes(tw.end) - keyToMinutes(tw.start);
  if (!Number.isFinite(durationMin) || durationMin <= 0) return null;
  return { date, startTime, durationMin: snapDuration(durationMin) };
}

/**
 * Read a window out of a restored logged-out publish buffer. Those snapshots
 * live in localStorage and are unversioned, so one written before the duration
 * model still carries `{ date, startTime, endTime }` and has to keep restoring.
 */
export function normalizeWindowState(raw: unknown): TimeWindowState | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Partial<TimeWindowState> & { endTime?: TimeStr };
  if (!w.date || !w.startTime) return null;
  if (typeof w.durationMin === 'number' && w.durationMin > 0) {
    return { date: w.date, startTime: w.startTime, durationMin: snapDuration(w.durationMin) };
  }
  if (!w.endTime) return null;
  // Legacy pair. Measure around the clock so an end that reads earlier than the
  // start restores as a short overnight window rather than a negative one.
  const span = (toMinutes(w.endTime) - toMinutes(w.startTime) + DAY_MIN) % DAY_MIN;
  return {
    date: w.date,
    startTime: w.startTime,
    durationMin: span ? snapDuration(span) : DEFAULT_DURATION_MIN,
  };
}

// --- Constraint computation ---

/**
 * The earliest datetime the user can pick for START.
 * - Create mode → now.
 * - Edit mode  → MIN(now, originalStart). If the original start has already
 *   passed, it stays valid (we never push existing values further back).
 */
function earliestStart(original?: OriginalTimeWindow): { date: DateStr; time: TimeStr } {
  const today = todayLocalDate();
  const now = nowLocalTime();
  if (!original) return { date: today, time: now };

  const nowKey = `${today}T${now}`;
  const origKey = `${original.date}T${original.startTime}`;
  return origKey < nowKey
    ? { date: original.date, time: original.startTime }
    : { date: today, time: now };
}

/**
 * The earliest instant the window may END, as a naive datetime key, or null in
 * create mode. An existing end that already passed stays valid; otherwise the
 * end has to be at or after now, so an edit can't retire a listing backwards.
 */
function earliestEndKey(original?: OriginalTimeWindow): string | null {
  if (!original) return null;
  const nowKey = `${todayLocalDate()}T${nowLocalTime()}`;
  const origKey = `${getEndDate(original)}T${getEndTime(original)}`;
  return origKey < nowKey ? origKey : nowKey;
}

/** Earliest selectable date. */
export function getMinDate(original?: OriginalTimeWindow): DateStr {
  return earliestStart(original).date;
}

/**
 * Earliest selectable START time on a given saleDate, or undefined when the
 * date is far enough in the future that no time floor applies.
 */
export function getMinStartTime(
  saleDate: DateStr,
  original?: OriginalTimeWindow,
): TimeStr | undefined {
  const e = earliestStart(original);
  if (saleDate <= e.date) return e.time;
  return undefined;
}

/**
 * The shortest window we'll accept for a given start, in minutes. Always at
 * least MIN_DURATION_MIN; in edit mode it also stretches far enough to reach
 * the edit-mode end floor, so an edit can't retire a listing backwards. The
 * form passes it to the duration wheel so the wheel can predict where a scroll
 * will settle (see clampDuration).
 */
export function getMinDurationMin(
  saleDate: DateStr,
  startTime: TimeStr,
  original?: OriginalTimeWindow,
  bounds: DurationBounds = PICKUP_DURATION_BOUNDS,
): number {
  let floor = bounds.minMin;
  const eeKey = earliestEndKey(original);
  if (eeKey) {
    const span = keyToMinutes(eeKey) - keyToMinutes(`${saleDate}T${startTime}`);
    if (span > floor) floor = span;
  }
  return Math.min(floor, bounds.maxMin);
}

/**
 * Hold a duration inside the wheel's range. Shared by clampWindow and by the
 * duration wheel itself, so the wheel can predict exactly where a scroll will
 * settle instead of waiting on a prop that may never change.
 */
export function clampDuration(
  durationMin: number,
  floorMin?: number,
  bounds: DurationBounds = PICKUP_DURATION_BOUNDS,
): number {
  const floor = floorMin ?? bounds.minMin;
  return Math.min(Math.max(durationMin, floor, bounds.minMin), bounds.maxMin);
}

// --- Defaults & clamping ---

/** Initial state for a fresh form (or edit form before existingListing loads). */
export function buildDefaultWindow(
  original?: OriginalTimeWindow,
  bounds: DurationBounds = PICKUP_DURATION_BOUNDS,
): TimeWindowState {
  if (original) return { ...original };
  const now = nowLocalTime();
  return {
    date: todayLocalDate(),
    startTime: now < DEFAULT_START_TIME ? DEFAULT_START_TIME : now,
    durationMin: bounds.defaultMin,
  };
}

/**
 * The recommended drop-off window for a listing that also offers pickup: it
 * opens the moment the pickup window closes, for the default run length.
 *
 * Starting exactly at the pickup end is deliberate and legal — both this app
 * and the backend treat windows as half-open, so touching bounds are adjacent
 * rather than overlapping. It also reads as the true sequence of the chef's
 * evening: they're home until 8:30, then they get in the car.
 */
export function buildDropoffWindowAfter(pickup: TimeWindowState): TimeWindowState {
  return windowStartingAfter(
    { ...pickup, durationMin: DROPOFF_DURATION_BOUNDS.defaultMin },
    pickup,
  );
}

/**
 * Slide `w` so it opens exactly when `other` closes, keeping its length and
 * carrying onto the next day if `other` ends after midnight.
 *
 * Only ever moves a window LATER, which matters: the alternative (placing the
 * new window before the existing one) reads more naturally for pickup-then-
 * drop-off, but it can land in the past, and clampWindow only pushes start
 * times forward — so it would be shoved back to now and straight into the
 * overlap it was supposed to resolve.
 */
export function windowStartingAfter(
  w: TimeWindowState,
  other: TimeWindowState,
): TimeWindowState {
  return { ...w, date: getEndDate(other), startTime: getEndTime(other) };
}

/** Absolute [start, end) of a window, in minutes-from-epoch. */
function windowRange(tw: TimeWindowState): { start: number; end: number } {
  const start = keyToMinutes(`${tw.date}T${tw.startTime}`);
  return { start, end: start + tw.durationMin };
}

/**
 * Sort comparator by absolute start instant, so a list of hand-off windows can
 * be shown in the order the chef's evening actually runs rather than in a fixed
 * pickup-then-drop-off order the times may not match.
 */
export function compareWindowStart(a: TimeWindowState, b: TimeWindowState): number {
  return windowRange(a).start - windowRange(b).start;
}

/**
 * Whether two windows collide. Half-open, so a drop-off starting exactly when
 * pickup ends is adjacent and allowed. Mirrors the backend's WindowsOverlap,
 * which is the authority — this one exists so the publish button can explain
 * the problem before the chef submits rather than after.
 */
export function windowsOverlap(a: TimeWindowState, b: TimeWindowState): boolean {
  const ra = windowRange(a);
  const rb = windowRange(b);
  return ra.start < rb.end && rb.start < ra.end;
}

/**
 * Force a proposed state to satisfy every constraint. Date and start time are
 * pushed UP only (never silently earlier than the user requested); the duration
 * is held inside the wheel's range.
 *
 * No duration option is ever rendered disabled. Cross-column validity on a
 * two-column wheel (12 hr against 45 min, or 0 hr against 00 min) can't be
 * expressed per-option without dead ends the user can't scroll out of, so the
 * wheel runs free and lands here instead: an out-of-range combination snaps to
 * the nearest legal one and the readout shows the result immediately.
 */
export function clampWindow(
  proposed: TimeWindowState,
  original?: OriginalTimeWindow,
  bounds: DurationBounds = PICKUP_DURATION_BOUNDS,
): TimeWindowState {
  let { date, startTime, durationMin } = proposed;

  const minDate = getMinDate(original);
  if (date < minDate) date = minDate;

  const minStart = getMinStartTime(date, original);
  if (minStart && startTime < minStart) startTime = minStart;

  durationMin = clampDuration(
    durationMin,
    getMinDurationMin(date, startTime, original, bounds),
    bounds,
  );

  return { date, startTime, durationMin };
}

// --- Readout ---

export interface WindowDescription {
  /** "6:00 PM" */
  startTime: string;
  /** "8:00 PM" */
  endTime: string;
  /** "Friday, August 28" — the whole window's day, when it stays on one day. */
  dateLong: string;
  /** "Fri, Aug 28" / "Sat, Aug 29" — labels each end separately when the
   *  window runs overnight and the two no longer share a date. */
  startDateShort: string;
  endDateShort: string;
  /** True when the end lands on the day after the start. */
  crossesMidnight: boolean;
}

/**
 * How the hand-off card names the window's day. Relative words come back as a
 * key rather than text because this file stays free of the i18n singleton (see
 * describeWindow); the caller translates. Everything else is already
 * locale-formatted by Intl.
 *
 * The today/tonight split matches the feed's rule in utils/timeWindow.ts: a
 * window starting at or after 5pm reads as "tonight".
 */
export interface WindowDateLabel {
  relativeKey: 'today' | 'tonight' | 'tomorrow' | null;
  /** Locale-formatted fallback ("Friday", "Apr 12") when relativeKey is null. */
  formatted: string;
}

const EVENING_HOUR = 17;

export function describeWindowDate(tw: TimeWindowState, locale: string): WindowDateLabel {
  const today = todayLocalDate();
  const tomorrow = addDays(today, 1);

  if (tw.date === today) {
    const hour = Number(tw.startTime.split(':')[0]);
    return { relativeKey: hour >= EVENING_HOUR ? 'tonight' : 'today', formatted: '' };
  }
  if (tw.date === tomorrow) {
    return { relativeKey: 'tomorrow', formatted: '' };
  }

  const d = toLocalDate(tw.date, tw.startTime);
  // Inside the next week a weekday alone is unambiguous; past that it needs a date.
  const withinWeek = tw.date < addDays(today, 7);
  const opts: Intl.DateTimeFormatOptions = withinWeek
    ? { weekday: 'long' }
    : { month: 'short', day: 'numeric' };
  return { relativeKey: null, formatted: new Intl.DateTimeFormat(locale, opts).format(d) };
}

/**
 * The window's length as the card states it: "2 hr", "30 min", "1 hr 15 min".
 * Suffixes are threaded in translated, same as the duration wheel's columns.
 */
export function describeDuration(
  durationMin: number,
  hoursSuffix: string,
  minutesSuffix: string,
): string {
  const { hours, minutes } = splitDuration(durationMin);
  if (hours === 0) return `${minutes} ${minutesSuffix}`;
  if (minutes === 0) return `${hours} ${hoursSuffix}`;
  return `${hours} ${hoursSuffix} ${minutes} ${minutesSuffix}`;
}

/**
 * The pieces the form's summary block renders. Returned as parts rather than
 * one string so the caller can give the times their own weight and attach a
 * date to each end only when the window actually needs two.
 *
 * Pure given `locale`, so this file stays free of the i18n singleton; the
 * caller supplies the translated "to" separator itself.
 */
export function describeWindow(tw: TimeWindowState, locale: string): WindowDescription {
  const time: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  const long: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  };
  const short: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };

  const start = toLocalDate(tw.date, tw.startTime);
  const end = toLocalDate(getEndDate(tw), getEndTime(tw));
  const fmt = (opts: Intl.DateTimeFormatOptions, d: Date) =>
    new Intl.DateTimeFormat(locale, opts).format(d);

  return {
    startTime: fmt(time, start),
    endTime: fmt(time, end),
    dateLong: fmt(long, start),
    startDateShort: fmt(short, start),
    endDateShort: fmt(short, end),
    crossesMidnight: endsNextDay(tw),
  };
}
